import React, { Component } from "react";
import Spinner from "../../LayOut/Spinner";
import { Button } from "devextreme-react";
import DateBox from "devextreme-react/date-box";
import SelectBox from "devextreme-react/select-box";
import DataGrid, {
  Column,
  Pager,
  Paging,
  SearchPanel,
  Export,
  Scrolling,
} from "devextreme-react/data-grid";
import { loadMessages, locale } from "devextreme/localization";
import { getPushReport } from "../../../apis/toowezeApi";
import { i18n, lgMessages, languageCode } from "../../../translate/i18n";
import { fixPortugueseText } from "../../../utils/fixPortugueseText";
import "./PushReportMiddle.css";

const TYPE_OPTIONS = [
  { value: "all", textKey: "reports.push.typeAll" },
  { value: "token", textKey: "reports.push.typeToken" },
  { value: "other", textKey: "reports.push.typeOther" },
];

function formatBrazilPhone(raw) {
  if (!raw) {
    return "";
  }
  let digits = String(raw).replace(/\D/g, "");
  if (digits.startsWith("55") && digits.length >= 12) {
    digits = digits.slice(2);
  }
  if (digits.startsWith("0") && digits.length >= 11) {
    digits = digits.slice(1);
  }
  if (digits.length === 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return digits || String(raw);
}

function isTokenPurpose(purpose) {
  return ["otp_transaction", "otp_signup", "otp"].includes(purpose);
}

export default class PushReportMiddle extends Component {
  constructor(props) {
    super(props);
    const monthDates = this.getMonthDates();
    this.state = {
      allRows: [],
      datagrid: [],
      spinner: false,
      error: null,
      info: null,
      customStartDate: monthDates.startOfMonth,
      customEndDate: monthDates.endOfMonth,
      messageType: "all",
      total: 0,
      tokenCount: 0,
      otherCount: 0,
    };
    this._requestSeq = 0;
    loadMessages(lgMessages);
    locale(languageCode);
  }

  componentWillUnmount() {
    this._requestSeq += 1;
  }

  returnDateValue(newDate) {
    return (
      newDate.getFullYear() +
      "-" +
      ("0" + (newDate.getMonth() + 1)).slice(-2) +
      "-" +
      ("0" + newDate.getDate()).slice(-2)
    );
  }

  getMonthDates() {
    const date = new Date();
    const initDate = new Date(date.getFullYear(), date.getMonth(), 1);
    const endDate = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    return {
      startOfMonth: this.returnDateValue(initDate) + "T00:00:00.00-03:00",
      endOfMonth: this.returnDateValue(endDate) + "T23:59:59.00-03:00",
    };
  }

  handleValueChange(event, type) {
    if (!event.value) {
      return;
    }
    const day = this.returnDateValue(new Date(event.value.toString()));
    if (type === "customStartDate") {
      this.setState({ customStartDate: day + "T00:00:00.00-03:00" });
    } else if (type === "customEndDate") {
      this.setState({ customEndDate: day + "T23:59:59.00-03:00" });
    }
  }

  applyLocalFilters = (allRows, messageType) => {
    const tokenCount = allRows.filter((r) => isTokenPurpose(r.purpose)).length;
    const otherCount = allRows.length - tokenCount;
    let datagrid = allRows;
    if (messageType === "token") {
      datagrid = allRows.filter((r) => isTokenPurpose(r.purpose));
    } else if (messageType === "other") {
      datagrid = allRows.filter((r) => !isTokenPurpose(r.purpose));
    }
    return { datagrid, tokenCount, otherCount };
  };

  onMessageTypeChange = (value) => {
    const filtered = this.applyLocalFilters(this.state.allRows, value);
    this.setState({
      messageType: value,
      ...filtered,
      info:
        filtered.datagrid.length === 0 && this.state.allRows.length > 0
          ? i18n.t("reports.push.emptyType")
          : null,
    });
  };

  fetchReport = () => {
    const { customStartDate, customEndDate, messageType } = this.state;

    if (!customStartDate || !customEndDate) {
      this.setState({ error: i18n.t("reports.push.datesRequired") });
      return;
    }

    const requestSeq = ++this._requestSeq;
    this.setState({ spinner: true, error: null, info: null });

    getPushReport(customStartDate, customEndDate)
      .then((result) => {
        if (requestSeq !== this._requestSeq) {
          return;
        }
        const allRows = (result.rows || []).map((row) => ({
          ...row,
          customerName: fixPortugueseText(row.customerName),
          subject: fixPortugueseText(row.subject),
          message: fixPortugueseText(row.message),
          receiverFormatted: formatBrazilPhone(row.mobil),
        }));
        const filtered = this.applyLocalFilters(allRows, messageType);
        this.setState({
          allRows,
          total: allRows.length,
          spinner: false,
          ...filtered,
          info:
            allRows.length === 0
              ? i18n.t("reports.push.empty")
              : filtered.datagrid.length === 0
                ? i18n.t("reports.push.emptyType")
                : null,
        });
      })
      .catch((error) => {
        if (requestSeq !== this._requestSeq) {
          return;
        }
        this.setState({
          allRows: [],
          datagrid: [],
          total: 0,
          tokenCount: 0,
          otherCount: 0,
          spinner: false,
          error:
            error?.response?.data?.msg ||
            error?.message ||
            i18n.t("reports.push.loadError"),
        });
      });
  };

  renderDate = (cell) => {
    if (!cell.value) {
      return "—";
    }
    try {
      const d = new Date(cell.value);
      if (Number.isNaN(d.getTime())) {
        return cell.value;
      }
      return d.toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "America/Sao_Paulo",
      });
    } catch (e) {
      return cell.value;
    }
  };

  renderPhone = (cell) => (
    <span className="push-report__phone">
      {cell.data?.receiverFormatted || formatBrazilPhone(cell.value) || "—"}
    </span>
  );

  renderPurpose = (cell) => {
    const map = {
      otp_transaction: i18n.t("reports.push.purposeOtpTxn"),
      otp_signup: i18n.t("reports.push.purposeOtpSignup"),
      otp: i18n.t("reports.push.tagToken"),
      other: i18n.t("reports.push.typeOther"),
    };
    const label = map[cell.value] || cell.value || "—";
    if (isTokenPurpose(cell.value)) {
      return <span className="push-report__badge">{label}</span>;
    }
    return <span className="push-report__muted">{label}</span>;
  };

  renderContent = (cell) => (
    <div className="push-report__msg" title={cell.value || undefined}>
      {cell.value || "—"}
    </div>
  );

  mapOptions(list) {
    return list.map((opt) => ({
      value: opt.value,
      text: i18n.t(opt.textKey),
    }));
  }

  render() {
    const {
      spinner,
      error,
      info,
      datagrid,
      total,
      tokenCount,
      otherCount,
      customStartDate,
      customEndDate,
      messageType,
    } = this.state;

    return (
      <div className="push-report">
        <div className="push-report__header">
          <h1 className="push-report__title">{i18n.t("reports.push.title")}</h1>
          <p className="push-report__hint">{i18n.t("reports.push.subtitle")}</p>
        </div>

        <div className="push-report__toolbar">
          <div className="push-report__field">
            <label>{i18n.t("transactions.activity.startDate")}</label>
            <DateBox
              type="date"
              value={customStartDate || null}
              onValueChanged={(event) =>
                this.handleValueChange(event, "customStartDate")
              }
              width="100%"
              height={36}
            />
          </div>
          <div className="push-report__field">
            <label>{i18n.t("transactions.activity.endDate")}</label>
            <DateBox
              type="date"
              value={customEndDate || null}
              onValueChanged={(event) =>
                this.handleValueChange(event, "customEndDate")
              }
              width="100%"
              height={36}
            />
          </div>
          <div className="push-report__field">
            <label>{i18n.t("reports.push.typeLabel")}</label>
            <SelectBox
              items={this.mapOptions(TYPE_OPTIONS)}
              value={messageType}
              displayExpr="text"
              valueExpr="value"
              onValueChanged={(e) => this.onMessageTypeChange(e.value)}
              width="100%"
              height={36}
            />
          </div>
          <div className="push-report__field push-report__field--btn">
            <label>&nbsp;</label>
            <Button
              text={i18n.t("reports.push.search")}
              type="default"
              stylingMode="contained"
              disabled={spinner}
              onClick={this.fetchReport}
              width="100%"
              height={36}
            />
          </div>
        </div>

        {spinner && <Spinner />}

        {!spinner && error && (
          <div className="alert alert-danger push-report__alert" role="alert">
            {error}
          </div>
        )}

        {!spinner && !error && info && (
          <div className="alert alert-info push-report__alert" role="alert">
            {info}
          </div>
        )}

        {!spinner && !error && total > 0 && (
          <>
            <div className="push-report__stats">
              <div className="push-report__stat">
                {i18n.t("reports.push.totalLabel")}
                <strong>{total}</strong>
              </div>
              <div className="push-report__stat push-report__stat--token">
                {i18n.t("reports.push.tokenCount")}
                <strong>{tokenCount}</strong>
              </div>
              <div className="push-report__stat push-report__stat--other">
                {i18n.t("reports.push.otherCount")}
                <strong>{otherCount}</strong>
              </div>
              {messageType !== "all" && (
                <div className="push-report__stat">
                  {i18n.t("reports.push.showing")}
                  <strong>{datagrid.length}</strong>
                </div>
              )}
            </div>

            <div className="push-report__grid">
              <DataGrid
                dataSource={datagrid}
                keyExpr="id"
                showBorders={false}
                showRowLines={true}
                showColumnLines={false}
                rowAlternationEnabled={true}
                wordWrapEnabled={true}
                columnAutoWidth={false}
                height={560}
                hoverStateEnabled={true}
                noDataText={i18n.t("reports.push.emptyType")}
              >
                <Scrolling mode="standard" useNative={true} showScrollbar="always" />
                <Paging defaultPageSize={20} />
                <Pager
                  showInfo={true}
                  showPageSizeSelector={true}
                  allowedPageSizes={[10, 20, 50]}
                />
                <SearchPanel
                  visible={true}
                  width={280}
                  placeholder={i18n.t("reports.push.searchPlaceholder")}
                />

                <Column
                  dataField="requestDate"
                  caption={i18n.t("reports.push.colDate")}
                  width={118}
                  cellRender={this.renderDate}
                  sortOrder="desc"
                />
                <Column
                  dataField="receiverFormatted"
                  caption={i18n.t("reports.push.colMobil")}
                  width={138}
                  cellRender={this.renderPhone}
                />
                <Column
                  dataField="customerName"
                  caption={i18n.t("reports.push.colCustomer")}
                  width={150}
                />
                <Column
                  dataField="purpose"
                  caption={i18n.t("reports.push.colPurpose")}
                  width={132}
                  cellRender={this.renderPurpose}
                />
                <Column
                  dataField="subject"
                  caption={i18n.t("reports.push.colSubject")}
                  width={190}
                />
                <Column
                  dataField="message"
                  caption={i18n.t("reports.push.colMessage")}
                  minWidth={280}
                  cellRender={this.renderContent}
                />
                <Column
                  dataField="eventType"
                  caption={i18n.t("reports.push.colEvent")}
                  width={80}
                />

                <Export enabled={true} fileName="TooWeze_Push_Report" />
              </DataGrid>
            </div>
          </>
        )}
      </div>
    );
  }
}
